import { useUser, useWorkTask, WorkTaskInfo } from "@tm/context-distribution"
import { useEffect } from "react"
import { useHistory, useRouteMatch, generatePath } from "react-router"
import { encodeUniqueId } from "@tm/utils"
import { Divider, Stack } from "@tm/components"
import { useCompilations } from "@tm/data/compilations"
import { isWM } from "../../../utils"
import { CompilationList } from "./CompilationList"
import { ArticleList } from "./ArticleList"
import { SingleFavoriteArticleList } from "./SingleFavoriteArticleList"
import { ModuleHeader } from "./ModuleHeader"
import { WmModuleHeader } from "./WmModuleHeader"
import { useFavoriteArticlesStore } from "./store"
import { EditDialog } from "./EditDialog"

export type FavoriteArticlesRouteParams = {
    listId?: string
}

type FavoriteArticlesProps = {
    onClose?(): void
}
export function FavoriteArticles({ onClose }: FavoriteArticlesProps) {
    const { workTask } = useWorkTask() ?? {}
    if (!workTask) {
        return null
    }
    return <FavoriteArticlesComponent onClose={onClose} workTask={workTask} />
}

function FavoriteArticlesComponent({ workTask, onClose }: FavoriteArticlesProps & { workTask: WorkTaskInfo }) {
    const { userContext } = useUser()
    const { query, setSelectedCompilation, compilationToEdit, setQuery } = useFavoriteArticlesStore()

    const { compilations, isLoading, error } = useCompilations(isWM(), query || undefined)
    const {
        path,
        params,
        params: { listId },
    } = useRouteMatch<FavoriteArticlesRouteParams>()
    const history = useHistory()

    useEffect(() => {
        if (compilations && !compilations.length && listId) {
            handleClickList()
        }
    }, [compilations, listId])

    useEffect(() => setSelectedCompilation(compilations?.find((x) => x.id === listId)), [listId, compilations])

    function handleClickList(id?: string) {
        const url = generatePath(path, { ...params, listId: id })
        history.push(url)
    }

    function handleCloseDetails() {
        setQuery("")
        handleClickList()
    }

    function handleOpenDetails(_listId: string) {
        window.document.querySelector(".module.worktask .worktask__content")?.scroll({ top: 0 })
        handleClickList(_listId)
    }

    function handleOpenExpandedOrOtherDetails() {
        if (!isWM() && compilations && compilations?.length > 1) {
            const toBeOpenedId = compilations.filter((c) => c.id !== listId).first()?.id
            return toBeOpenedId && handleOpenDetails(toBeOpenedId)
        }
        return handleClickList()
    }

    function handleCloseButtonClick() {
        setQuery("")
        onClose?.()
    }

    const encodedWorkTaskId = encodeUniqueId(workTask.id)

    // Use for anonym login (Industy Catalog) a fullscreen Compilationlist
    if (userContext.isAnonym) {
        return <SingleFavoriteArticleList compilationId={compilations?.first()?.id} />
    }

    return (
        <Stack gap={2} flex={1} height="100%" position="relative">
            {isWM() ? <WmModuleHeader onClose={onClose} onCloseDetails={handleCloseDetails} /> : <ModuleHeader onClose={handleCloseButtonClick} />}

            <Stack direction="row" gap={1} pt={1} px={2} divider={<Divider orientation="vertical" />} sx={{ minHeight: 0 }}>
                <CompilationList
                    compilationId={listId}
                    compilations={compilations}
                    loading={isLoading}
                    error={error}
                    onOpenDetails={handleOpenDetails}
                    onCloseDetails={handleCloseDetails}
                />
                {listId && (
                    <ArticleList
                        compilationId={listId}
                        encodedWorkTaskId={encodedWorkTaskId}
                        onOpenCompilationList={handleOpenExpandedOrOtherDetails}
                        onArticleDetailsOpened={onClose}
                    />
                )}
            </Stack>
            {compilationToEdit && <EditDialog editFavoriteList={compilationToEdit} onOpenCompilationDetails={handleOpenDetails} />}
        </Stack>
    )
}
